import type { Classification } from '@rp/contracts'
import { Button, Card, Chip, ConfidenceMeter } from '../../design'
import { CLASSIFICATION_SOURCE_LABELS, INTENT_LABELS, LANGUAGE_LABELS, intentTone } from '../queue/labels'
import { ITEM_COPY } from './copy'

const domainLabel = (inDomain: boolean): string => (inDomain ? ITEM_COPY.inDomain : ITEM_COPY.outOfDomain)

const Facts = ({ classification }: { classification: Classification }) => (
  <>
    {classification.intent === null ? null : (
      <Chip tone={intentTone(classification.intent)}>{INTENT_LABELS[classification.intent]}</Chip>
    )}
    {classification.language === null ? null : (
      <span className="classification-detail">{LANGUAGE_LABELS[classification.language]}</span>
    )}
    {classification.inDomain === null ? null : (
      <span className="classification-detail">{domainLabel(classification.inDomain)}</span>
    )}
  </>
)

const UnclassifiedNotes = ({ error }: { error: string | null }) => (
  <>
    <p className="classification-hint">{ITEM_COPY.unclassifiedHint}</p>
    {error === null ? null : <p className="classification-error tone-warning">{error}</p>}
  </>
)

const Verdict = ({ classification }: { classification: Classification }) => {
  if (classification.source === null) return null
  const machine = classification.source !== 'human'
  return (
    <div className={`classification-band${machine ? ' classification-verdict-machine' : ''}`}>
      <Chip tone={machine ? 'info' : 'neutral'} variant="outline">
        {CLASSIFICATION_SOURCE_LABELS[classification.source]}
      </Chip>
      {classification.rule === null ? null : <code className="classification-rule">{classification.rule}</code>}
      {classification.confidence === null ? null : (
        <>
          <span className="classification-detail">{ITEM_COPY.confidence}</span>
          <ConfidenceMeter value={classification.confidence} label={ITEM_COPY.confidence} />
        </>
      )}
    </div>
  )
}

export const ClassificationPanel = ({
  classification,
  onReclassify,
  canReclassify,
}: {
  classification: Classification
  onReclassify: () => void
  canReclassify: boolean
}) => {
  const failed = classification.status === 'failed'
  return (
    <Card as="section" className="classification">
      <div className="classification-band classification-facts">
        <h2 className="classification-title">{ITEM_COPY.classification}</h2>
        {failed ? (
          <Chip tone="warning" variant="solid">
            {ITEM_COPY.unclassified}
          </Chip>
        ) : (
          <Facts classification={classification} />
        )}
        <Button className="classification-reclassify" onClick={onReclassify} disabled={!canReclassify}>
          {ITEM_COPY.reclassify}
        </Button>
        {failed ? <UnclassifiedNotes error={classification.error} /> : null}
      </div>
      <Verdict classification={classification} />
    </Card>
  )
}
